import { ChatLevelUp, ChatSuggestion, DraftInfo } from '@/types/chat';
export interface SSEEvent {
  type: string;
  [key: string]: unknown;
}

export interface ToolStartEvent {
  type: 'tool_start';
  tool_name: string;
  label?: string;
}

export interface ToolEndEvent {
  type: 'tool_end';
  tool_name: string;
}

export interface TextDeltaEvent {
  type: 'text_delta';
  delta: string;
}

export interface ResultEvent {
  type: 'result';
  value: FinalResultArgs;
}

export interface SuggestionsEvent {
  type: 'next_steps';
  value: ChatSuggestion[];
}

export interface LevelUpEvent {
  type: 'level_up';
  value: ChatLevelUp[];
}

export interface DraftSavedEvent {
  type: 'draft';
  value: DraftInfo;
}

export interface ErrorEvent {
  type: 'error';
  message?: string;
}

type AnyRecord = Record<string, unknown>;

const isRecord = (v: unknown): v is AnyRecord => Boolean(v) && typeof v === 'object';

const getString = (obj: AnyRecord, key: string) => {
  const v = obj[key];
  return typeof v === 'string' ? v : undefined;
};

/**
 * Citation entry from the final_result tool call. Use `internal_document_id` (the real document
 * id); `document_id` is a display id like "UPLOAD_0". Older responses lack the former — fall back
 * only when `document_id` is a UUID.
 */
export interface StreamingCitationEntry {
  citation_id: number;
  document_type: string;
  internal_document_id?: string;
  document_id: string;
  quote: string;
  context: string | null;
}

/** Final response payload (as sent by the backend). */
export interface FinalResultArgs {
  full_output: string;
  citation_index: StreamingCitationEntry[];
}

export interface StreamingChatCallbacks {
  onFinalResult?: (args: FinalResultArgs) => void;
  onFinalResultPartial?: (args: { full_output: string; partial: boolean }) => void;
  onError?: (error: Error) => void;
  onToolStart?: (event: ToolStartEvent) => void;
  onToolEnd?: (event: ToolEndEvent) => void;
  onSuggestions?: (event: SuggestionsEvent) => void;
  onDraftSaved?: (event: DraftSavedEvent) => void;
  onLevelUp?: (event: LevelUpEvent) => void;
}

type SseProcessResult = 'continue' | 'done';

const processSseLines = (
  lines: string[],
  callbacks: StreamingChatCallbacks,
  streamedText: { value: string },
): SseProcessResult => {
  for (const line of lines) {
    if (!line.startsWith('data: ')) continue;

    const data = line.slice(6);

    if (data === '[DONE]') {
      return 'done';
    }

    try {
      const parsed = JSON.parse(data) as unknown;
      if (!isRecord(parsed)) continue;
      const type = getString(parsed, 'type');
      if (!type) continue;

      switch (type) {
        case 'tool_start': {
          const tool_name = getString(parsed, 'tool_name');
          if (!tool_name || !callbacks.onToolStart) break;
          const label = getString(parsed, 'label');
          callbacks.onToolStart({ type: 'tool_start', tool_name, ...(label ? { label } : {}) });
          break;
        }

        case 'tool_end': {
          const tool_name = getString(parsed, 'tool_name');
          if (!tool_name || !callbacks.onToolEnd) break;
          callbacks.onToolEnd({ type: 'tool_end', tool_name });
          break;
        }

        case 'text_delta': {
          const delta = getString(parsed, 'delta');
          if (!delta) break;
          streamedText.value += delta;
          callbacks.onFinalResultPartial?.({ full_output: streamedText.value, partial: true });
          break;
        }

        case 'result': {
          const valueRaw = parsed.value as unknown;
          const value = isRecord(valueRaw) ? (valueRaw as Partial<FinalResultArgs>) : undefined;
          if (!value || typeof value.full_output !== 'string') break;

          const finalArgs: FinalResultArgs = {
            full_output: value.full_output,
            citation_index: Array.isArray(value.citation_index)
              ? (value.citation_index as StreamingCitationEntry[])
              : [],
          };
          callbacks.onFinalResultPartial?.({ full_output: finalArgs.full_output, partial: false });
          callbacks.onFinalResult?.(finalArgs);
          break;
        }

        case 'next_steps':
          if (callbacks.onSuggestions) {
            const value = parsed.value as unknown;
            callbacks.onSuggestions({
              type: 'next_steps',
              value: value as ChatSuggestion[],
            });
          }
          break;

        case 'level_up':
          if (callbacks.onLevelUp) {
            const value = parsed.value as unknown;
            callbacks.onLevelUp({ type: 'level_up', value: value as ChatLevelUp[] });
          }
          break;

        case 'draft':
          if (callbacks.onDraftSaved) {
            const value = parsed.value as unknown;
            callbacks.onDraftSaved({ type: 'draft', value: value as DraftInfo });
          }
          break;

        case 'error': {
          const message = getString(parsed, 'message') ?? 'Unknown streaming error';
          callbacks.onError?.(new Error(message));
          return 'done';
        }

        default:
          break;
      }
    } catch (parseError) {
      console.warn('Failed to parse SSE event:', data, parseError);
    }
  }

  return 'continue';
};

export const streamChatResponse = async (
  url: string,
  body: unknown,
  callbacks: StreamingChatCallbacks,
  signal: AbortSignal,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let lineBuffer = '';
    let processedLength = 0;
    const streamedText = { value: '' };
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', onAbort);
      fn();
    };

    const appendAndProcess = (responseText: string): SseProcessResult => {
      const chunk = responseText.slice(processedLength);
      if (!chunk) return 'continue';
      processedLength = responseText.length;

      lineBuffer += chunk;
      const lines = lineBuffer.split('\n');
      lineBuffer = lines.pop() || '';
      return processSseLines(lines, callbacks, streamedText);
    };

    const onAbort = () => {
      if (xhr.readyState !== XMLHttpRequest.UNSENT && xhr.readyState !== XMLHttpRequest.DONE) {
        xhr.abort();
      }
    };

    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('fletch-api-key', `${import.meta.env.VITE_FLETCH_API_KEY}`);

    xhr.onprogress = () => {
      if (signal.aborted) return;
      if (appendAndProcess(xhr.responseText) === 'done') {
        settle(resolve);
      }
    };

    xhr.onload = () => {
      if (signal.aborted) {
        settle(resolve);
        return;
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        settle(() => reject(new Error(`HTTP error! status: ${xhr.status}`)));
        return;
      }

      appendAndProcess(xhr.responseText);
      settle(resolve);
    };

    xhr.onerror = () => {
      if (signal.aborted) {
        settle(resolve);
        return;
      }
      settle(() => reject(new Error('Network error while streaming chat response')));
    };

    xhr.onabort = () => {
      settle(resolve);
    };

    signal.addEventListener('abort', onAbort);

    if (signal.aborted) {
      settle(resolve);
      return;
    }

    xhr.send(JSON.stringify(body));
  });
};
